import React, { useState, useEffect, useRef } from 'react';
import {
  Save, RefreshCw, Upload, Trash2, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
} from '../app/api';

const Settings = () => {
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  // ✅ RTK Query — auto cache
  const { data, isLoading } = useGetSettingsQuery();
  const [updateSettings, { isLoading: saving }] = useUpdateSettingsMutation();

  const [imageBaseUrl, setImageBaseUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);

  // ✅ Local state (form ke liye)
  const [settings, setSettings] = useState({
    general: {
      storeName: '', storeEmail: '', storePhone: '', storeAddress: '',
      storeTagline: '', storeLogo: '', favicon: '',
      timezone: 'Asia/Kolkata', currency: 'INR', dateFormat: 'DD/MM/YYYY',
    },
    business: {
      taxRate: 0, shippingCost: 0, freeShippingThreshold: 0, minOrderAmount: 0,
      maxOrderAmount: 0, returnPolicy: '', exchangePolicy: '', warrantyPeriod: '',
    },
    payment: {
      upiEnabled: false, upiId: '', cardEnabled: false,
      cardPaymentGateway: 'Razorpay', cardTestMode: false,
      cardApiKey: '', cardApiSecret: '', netBankingEnabled: false,
      codEnabled: false, codExtraCharge: 0, walletEnabled: false,
    },
    shipping: {
      domesticEnabled: false, domesticRate: 0, domesticFreeThreshold: 0,
      internationalEnabled: false, internationalRate: 0,
      internationalFreeThreshold: 0, deliveryDays: '3-5',
      sameDayDelivery: false, expressDelivery: false, expressCharge: 0,
    },
    notifications: {
      orderConfirmation: false, orderStatusUpdate: false, paymentReceived: false,
      shipmentUpdate: false, deliveryConfirmation: false, lowStockAlert: false,
      reviewReceived: false, customerRegistration: false,
      marketingEmails: false, promoNotifications: false,
    },
    security: {
      twoFactorAuth: false, sessionTimeout: 60, maxLoginAttempts: 5,
      passwordExpiry: 90, ipWhitelist: '', allowedIPs: [],
      sslEnabled: true, backupEnabled: true, backupFrequency: 'daily',
    },
    appearance: {
      theme: 'light', primaryColor: '#2E7D32', secondaryColor: '#FF6F00',
      fontFamily: 'Inter', sidebarCollapsed: false, compactMode: false,
      showBreadcrumbs: true, showNotifications: true,
    },
  });

  // ✅ Sync RTK Query data → local state
  useEffect(() => {
    if (data?.data?.settings) {
      const s = data.data.settings;
      setImageBaseUrl(data.data.imageBaseUrl || '');

      setSettings((prev) => ({
        general: { ...prev.general, ...(s.general || {}) },
        business: { ...prev.business, ...(s.business || {}) },
        payment: { ...prev.payment, ...(s.payment || {}) },
        shipping: { ...prev.shipping, ...(s.shipping || {}) },
        notifications: { ...prev.notifications, ...(s.notifications || {}) },
        security: { ...prev.security, ...(s.security || {}) },
        appearance: { ...prev.appearance, ...(s.appearance || {}) },
      }));
    }
  }, [data]);

  // ============================================
  // HELPERS
  // ============================================
  const getFullImage = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${imageBaseUrl}${path}`;
  };

  const updateSection = (section, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  // ============================================
  // FILE HANDLERS
  // ============================================
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WEBP allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5MB allowed');
      return;
    }
    setLogoFile(file);
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFaviconFile(file);
  };

  const removeLogo = () => {
    setLogoFile(null);
    updateSection('general', 'storeLogo', '');
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const removeFavicon = () => {
    setFaviconFile(null);
    updateSection('general', 'favicon', '');
    if (faviconInputRef.current) faviconInputRef.current.value = '';
  };

  // ============================================
  // SAVE
  // ============================================
  const handleSave = async () => {
    try {
      const fd = new FormData();

      Object.keys(settings).forEach((section) => {
        const sectionData = { ...settings[section] };
        if (section === 'general') {
          delete sectionData.storeLogo;
          delete sectionData.favicon;
        }
        fd.append(section, JSON.stringify(sectionData));
      });

      if (logoFile) fd.append('storeLogo', logoFile);
      if (faviconFile) fd.append('favicon', faviconFile);

      await updateSettings(fd).unwrap();
      toast.success('Settings saved successfully!');
      setLogoFile(null);
      setFaviconFile(null);
      // ✅ Auto refetch via invalidatesTags
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to save settings');
    }
  };

  // ============================================
  // RESET
  // ============================================
  const handleReset = async () => {
    if (!window.confirm('Reset all settings to default?')) return;
    try {
      const fd = new FormData();
      // Send empty to trigger reset (optional — depends on backend)
      await updateSettings(fd).unwrap();
      toast.success('Settings reset');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to reset settings');
    }
  };

  // ============================================
  // TOGGLE COMPONENT
  // ============================================
  const Toggle = ({ value, onChange, label }) => (
    <button
      type="button"
      className={`toggle-switch ${value ? 'active' : ''}`}
      onClick={() => onChange(!value)}
    >
      {value ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
      <span>{label}</span>
    </button>
  );

  // ============================================
  // LOADING
  // ============================================
  if (isLoading) {
    return (
      <Layout>
        <div className="flex-center" style={{ height: '60vh' }}>
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <Layout>
      <div className="settings-page">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Settings</h1>
            <p className="page-subtitle">Manage all store configurations</p>
          </div>
          <div className="page-actions">
            <button type="button" className="btn btn-outline" onClick={handleReset} disabled={saving}>
              <RefreshCw size={16} />
              Reset All
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
              <Save size={18} />
              {saving ? 'Saving...' : 'Save All Settings'}
            </button>
          </div>
        </div>

        {/* GENERAL */}
        <div className="card settings-section">
          <h3 className="section-title">General</h3>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Store Name</label>
              <input type="text" className="form-control" value={settings.general.storeName} onChange={(e) => updateSection('general', 'storeName', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Store Email</label>
              <input type="email" className="form-control" value={settings.general.storeEmail} onChange={(e) => updateSection('general', 'storeEmail', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input type="tel" className="form-control" value={settings.general.storePhone} onChange={(e) => updateSection('general', 'storePhone', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Tagline</label>
              <input type="text" className="form-control" value={settings.general.storeTagline} onChange={(e) => updateSection('general', 'storeTagline', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea className="form-control" rows="2" value={settings.general.storeAddress} onChange={(e) => updateSection('general', 'storeAddress', e.target.value)} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Store Logo</label>
              <div className="logo-upload">
                <div className="logo-preview">
                  {logoFile ? (
                    <img src={URL.createObjectURL(logoFile)} alt="Logo" />
                  ) : settings.general.storeLogo ? (
                    <img src={getFullImage(settings.general.storeLogo)} alt="Logo" />
                  ) : (
                    <span>🌿</span>
                  )}
                </div>
                <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
                <button type="button" className="btn btn-outline btn-sm" onClick={() => logoInputRef.current?.click()}>
                  <Upload size={16} /> Upload
                </button>
                {(logoFile || settings.general.storeLogo) && (
                  <button type="button" className="btn btn-outline btn-sm btn-danger" onClick={removeLogo}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Favicon</label>
              <div className="logo-upload">
                <div className="logo-preview" style={{ width: 40, height: 40 }}>
                  {faviconFile ? (
                    <img src={URL.createObjectURL(faviconFile)} alt="Favicon" />
                  ) : settings.general.favicon ? (
                    <img src={getFullImage(settings.general.favicon)} alt="Favicon" />
                  ) : (
                    <span>🌿</span>
                  )}
                </div>
                <input ref={faviconInputRef} type="file" accept="image/*,.ico" onChange={handleFaviconChange} style={{ display: 'none' }} />
                <button type="button" className="btn btn-outline btn-sm" onClick={() => faviconInputRef.current?.click()}>
                  <Upload size={16} /> Upload
                </button>
                {(faviconFile || settings.general.favicon) && (
                  <button type="button" className="btn btn-outline btn-sm btn-danger" onClick={removeFavicon}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Timezone</label>
              <select className="form-control" value={settings.general.timezone} onChange={(e) => updateSection('general', 'timezone', e.target.value)}>
                <option value="Asia/Kolkata">Asia/Kolkata</option>
                <option value="Asia/Dubai">Asia/Dubai</option>
                <option value="Europe/London">Europe/London</option>
                <option value="America/New_York">America/New_York</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Currency</label>
              <select className="form-control" value={settings.general.currency} onChange={(e) => updateSection('general', 'currency', e.target.value)}>
                <option value="INR">INR ₹</option>
                <option value="USD">USD $</option>
                <option value="EUR">EUR €</option>
                <option value="GBP">GBP £</option>
              </select>
            </div>
          </div>
        </div>

        {/* BUSINESS */}
        <div className="card settings-section">
          <h3 className="section-title">Business</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Tax Rate (%)</label>
              <input type="number" className="form-control" value={settings.business.taxRate} onChange={(e) => updateSection('business', 'taxRate', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="form-group">
              <label className="form-label">Shipping Cost (₹)</label>
              <input type="number" className="form-control" value={settings.business.shippingCost} onChange={(e) => updateSection('business', 'shippingCost', parseFloat(e.target.value) || 0)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Free Shipping Threshold (₹)</label>
              <input type="number" className="form-control" value={settings.business.freeShippingThreshold} onChange={(e) => updateSection('business', 'freeShippingThreshold', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="form-group">
              <label className="form-label">Min Order Amount (₹)</label>
              <input type="number" className="form-control" value={settings.business.minOrderAmount} onChange={(e) => updateSection('business', 'minOrderAmount', parseFloat(e.target.value) || 0)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Return Policy</label>
              <input type="text" className="form-control" value={settings.business.returnPolicy} onChange={(e) => updateSection('business', 'returnPolicy', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Exchange Policy</label>
              <input type="text" className="form-control" value={settings.business.exchangePolicy} onChange={(e) => updateSection('business', 'exchangePolicy', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Warranty Period</label>
            <input type="text" className="form-control" value={settings.business.warrantyPeriod} onChange={(e) => updateSection('business', 'warrantyPeriod', e.target.value)} />
          </div>
        </div>

        {/* PAYMENT */}
        <div className="card settings-section">
          <h3 className="section-title">Payment</h3>
          <div className="form-row">
            <div className="form-group">
              <Toggle value={settings.payment.upiEnabled} onChange={(v) => updateSection('payment', 'upiEnabled', v)} label="Enable UPI" />
              {settings.payment.upiEnabled && (
                <input type="text" className="form-control" placeholder="UPI ID" value={settings.payment.upiId} onChange={(e) => updateSection('payment', 'upiId', e.target.value)} style={{ marginTop: 8 }} />
              )}
            </div>
            <div className="form-group">
              <Toggle value={settings.payment.cardEnabled} onChange={(v) => updateSection('payment', 'cardEnabled', v)} label="Enable Card" />
            </div>
          </div>
          {settings.payment.cardEnabled && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Gateway</label>
                <select className="form-control" value={settings.payment.cardPaymentGateway} onChange={(e) => updateSection('payment', 'cardPaymentGateway', e.target.value)}>
                  <option value="Razorpay">Razorpay</option>
                  <option value="Stripe">Stripe</option>
                  <option value="PayPal">PayPal</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">API Key</label>
                <input type="text" className="form-control" value={settings.payment.cardApiKey} onChange={(e) => updateSection('payment', 'cardApiKey', e.target.value)} />
              </div>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <Toggle value={settings.payment.netBankingEnabled} onChange={(v) => updateSection('payment', 'netBankingEnabled', v)} label="Enable Net Banking" />
            </div>
            <div className="form-group">
              <Toggle value={settings.payment.codEnabled} onChange={(v) => updateSection('payment', 'codEnabled', v)} label="Enable COD" />
              {settings.payment.codEnabled && (
                <input type="number" className="form-control" placeholder="Extra Charge ₹" value={settings.payment.codExtraCharge} onChange={(e) => updateSection('payment', 'codExtraCharge', parseFloat(e.target.value) || 0)} style={{ marginTop: 8 }} />
              )}
            </div>
          </div>
        </div>

        {/* SHIPPING */}
        <div className="card settings-section">
          <h3 className="section-title">Shipping</h3>
          <div className="form-row">
            <div className="form-group">
              <Toggle value={settings.shipping.domesticEnabled} onChange={(v) => updateSection('shipping', 'domesticEnabled', v)} label="Domestic Shipping" />
              {settings.shipping.domesticEnabled && (
                <div className="form-row" style={{ marginTop: 8 }}>
                  <input type="number" className="form-control" placeholder="Rate ₹" value={settings.shipping.domesticRate} onChange={(e) => updateSection('shipping', 'domesticRate', parseFloat(e.target.value) || 0)} />
                  <input type="number" className="form-control" placeholder="Free above ₹" value={settings.shipping.domesticFreeThreshold} onChange={(e) => updateSection('shipping', 'domesticFreeThreshold', parseFloat(e.target.value) || 0)} />
                </div>
              )}
            </div>
            <div className="form-group">
              <Toggle value={settings.shipping.internationalEnabled} onChange={(v) => updateSection('shipping', 'internationalEnabled', v)} label="International Shipping" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <Toggle value={settings.shipping.expressDelivery} onChange={(v) => updateSection('shipping', 'expressDelivery', v)} label="Express Delivery" />
            </div>
            <div className="form-group">
              <label className="form-label">Delivery Days</label>
              <select className="form-control" value={settings.shipping.deliveryDays} onChange={(e) => updateSection('shipping', 'deliveryDays', e.target.value)}>
                <option value="1-2">1-2 Days</option>
                <option value="2-3">2-3 Days</option>
                <option value="3-5">3-5 Days</option>
                <option value="5-7">5-7 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* NOTIFICATIONS */}
        <div className="card settings-section">
          <h3 className="section-title">Notifications</h3>
          <div className="checkbox-grid">
            {[
              { key: 'orderConfirmation', label: 'Order Confirmation' },
              { key: 'orderStatusUpdate', label: 'Order Status Updates' },
              { key: 'paymentReceived', label: 'Payment Received' },
              { key: 'shipmentUpdate', label: 'Shipment Updates' },
              { key: 'deliveryConfirmation', label: 'Delivery Confirmation' },
              { key: 'lowStockAlert', label: 'Low Stock Alerts' },
              { key: 'reviewReceived', label: 'New Reviews' },
              { key: 'customerRegistration', label: 'New Customer' },
              { key: 'marketingEmails', label: 'Marketing Emails' },
              { key: 'promoNotifications', label: 'Promo Notifications' },
            ].map((item) => (
              <Toggle key={item.key} value={settings.notifications[item.key]} onChange={(v) => updateSection('notifications', item.key, v)} label={item.label} />
            ))}
          </div>
        </div>

        {/* SECURITY */}
        <div className="card settings-section">
          <h3 className="section-title">Security</h3>
          <div className="form-row">
            <div className="form-group">
              <Toggle value={settings.security.twoFactorAuth} onChange={(v) => updateSection('security', 'twoFactorAuth', v)} label="Two-Factor Authentication" />
            </div>
            <div className="form-group">
              <Toggle value={settings.security.sslEnabled} onChange={(v) => updateSection('security', 'sslEnabled', v)} label="Enable SSL" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Session Timeout (minutes)</label>
              <input type="number" className="form-control" value={settings.security.sessionTimeout} onChange={(e) => updateSection('security', 'sessionTimeout', parseInt(e.target.value) || 0)} />
            </div>
            <div className="form-group">
              <label className="form-label">Max Login Attempts</label>
              <input type="number" className="form-control" value={settings.security.maxLoginAttempts} onChange={(e) => updateSection('security', 'maxLoginAttempts', parseInt(e.target.value) || 0)} />
            </div>
          </div>
        </div>

        {/* APPEARANCE */}
        <div className="card settings-section">
          <h3 className="section-title">Appearance</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Theme</label>
              <select className="form-control" value={settings.appearance.theme} onChange={(e) => updateSection('appearance', 'theme', e.target.value)}>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Font</label>
              <select className="form-control" value={settings.appearance.fontFamily} onChange={(e) => updateSection('appearance', 'fontFamily', e.target.value)}>
                <option value="Inter">Inter</option>
                <option value="Roboto">Roboto</option>
                <option value="Poppins">Poppins</option>
                <option value="Montserrat">Montserrat</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Primary Color</label>
              <input type="color" value={settings.appearance.primaryColor} onChange={(e) => updateSection('appearance', 'primaryColor', e.target.value)} className="color-picker" />
            </div>
            <div className="form-group">
              <label className="form-label">Secondary Color</label>
              <input type="color" value={settings.appearance.secondaryColor} onChange={(e) => updateSection('appearance', 'secondaryColor', e.target.value)} className="color-picker" />
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="form-actions sticky-actions">
          <button type="button" className="btn btn-outline" onClick={handleReset} disabled={saving}>
            <RefreshCw size={16} />
            Reset
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={18} />
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;